import {useCallback, useEffect, useRef, useState} from 'react'

// A textarea that keeps what the user is typing in its OWN state and pushes it
// up on a short debounce (and immediately on blur / unmount).
//
// Why: the edit modal's text fields were controlled straight off `editingPost`,
// so every keystroke re-rendered all of GroupsWrapper — the posts table, the
// group lists, the lot. On a phone that takes long enough that React writes a
// STALE value back into the field while the browser is still delivering input:
// the caret snaps to the old offset and the rest of the paste lands there. The
// result is text spliced mid-word into the wrong place ("Audi Vir" in one line,
// "tual Cockpit" six lines down) — customers reported it as "the words get
// mixed up when I paste from my phone". Long pastes arrive in chunks, and so do
// dictation and autocorrect, which is why it showed up on phones and not on a
// desktop.
//
// Holding the value locally means the giant re-render happens ~once per pause
// instead of once per character, so there is no stale write-back to race with.
//
// The value still flows down: an edit made ELSEWHERE (accepting an AI
// suggestion, inserting a WhatsApp link, a phone stripped by the caller,
// switching to another post) changes `value`, and we adopt it — but only while
// no local edit is waiting to be pushed, or we'd clobber what the user just
// typed with the value from before their keystroke.
type Props = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string
  onCommit: (next: string) => void
  delay?: number
}

export const DebouncedTextarea = ({value, onCommit, delay = 250, onBlur, ...rest}: Props) => {
  const [local, setLocal] = useState(value)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Latest values for the unmount flush, which runs outside this render's closure.
  const localRef = useRef(local)
  const commitRef = useRef(onCommit)
  localRef.current = local
  commitRef.current = onCommit

  const flush = useCallback(() => {
    if (!timerRef.current) return
    clearTimeout(timerRef.current)
    timerRef.current = null
    commitRef.current(localRef.current)
  }, [])

  // Adopt an external change, but never on top of a pending local edit.
  useEffect(() => {
    if (timerRef.current) return
    setLocal((cur) => (cur === value ? cur : value))
  }, [value])

  // A pending edit must not be lost when the modal closes or the post changes.
  useEffect(() => () => flush(), [flush])

  return (
    <textarea
      {...rest}
      value={local}
      onChange={(e) => {
        const next = e.target.value
        setLocal(next)
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
          timerRef.current = null
          commitRef.current(next)
        }, delay)
      }}
      onBlur={(e) => {
        // Clicking Save blurs the field first (mousedown), so this lands before
        // the click handler reads the post — no debounce wait on the way out.
        flush()
        onBlur?.(e)
      }}
    />
  )
}

export default DebouncedTextarea
